import React, { useMemo } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Box
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FiBookOpen } from 'react-icons/fi';
import {
  CHART_OF_ACCOUNTS, 
  IRS_990_CATEGORIES,
  FISCAL_SPONSOR_BASE_ACCOUNTS 
} from '../../../../../utils/accountingCodes';
import { AccountingCategorizationValues } from '../../../../types/accounting';

interface AccountingCategorizationProps {
  initialValues: AccountingCategorizationValues;
  onCategoryChange: (values: AccountingCategorizationValues) => void;
  lineItemDescription: string;
  selectedAccount: string;
  amount: number;
}

interface AccountSubcategory {
  code: string;
  name: string;
}

interface AccountCategory {
  code: string;
  name: string;
  subcategories?: AccountSubcategory[];
}

interface ExpenseCategory {
  [key: string]: AccountCategory[];
}

type ChartOfAccounts = {
  Assets: AccountCategory[];
  Liabilities: AccountCategory[];
  'Net Assets': AccountCategory[];
  Revenue: AccountCategory[];
  Expenses: ExpenseCategory;
}

const CategoryPaper = styled(Paper)(({ theme }) => ({
  background: 'rgb(26, 31, 46)', // Dark navy background
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  position: 'relative',
  overflow: 'hidden',
  padding: theme.spacing(3),
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #0EA5E9 0%, #10B981 100%)'
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgb(34, 39, 58)', // Slightly lighter than background
    borderRadius: '8px',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.12)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0EA5E9',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiInputBase-input': {
    color: 'rgba(255, 255, 255, 0.9)',
  },
}));

const HeaderTypography = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.9)',
  fontWeight: 600,
  letterSpacing: '-0.02em',
  marginBottom: theme.spacing(3),
}));

const StyledIcon = styled(FiBookOpen)({
  color: '#10B981',
  fontSize: '24px'
});

const StyledMenuItem = styled(MenuItem)(({ theme, nested }: { theme: any, nested?: boolean }) => ({
  color: nested ? '#94A3B8' : '#E2E8F0',
  padding: '10px 16px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    color: '#34D399',
  },
  '&.Mui-selected': {
    backgroundColor: 'rgba(16, 185, 129, 0.15)',
    color: '#6EE7B7',
    '&:hover': {
      backgroundColor: 'rgba(16, 185, 129, 0.2)',
    }
  }
}));

export const AccountingCategorization: React.FC<AccountingCategorizationProps> = ({
  initialValues,
  onCategoryChange,
  lineItemDescription,
  selectedAccount,
  amount
}) => {
  const handleChange = (field: keyof AccountingCategorizationValues, value: string) => {
    const newValues = { ...initialValues, [field]: value };

    // Reset dependent fields when main category changes
    if (field === 'accountCategory') {
      newValues.accountSubcategory = '';
      newValues.form990Category = '';
      newValues.functionType = '';
    }

    // Auto-select function type based on category if applicable
    if (field === 'accountSubcategory') {
      if (value.startsWith('5')) newValues.functionType = 'PROGRAM';
      else if (value.startsWith('6')) newValues.functionType = 'ADMIN';
      else if (value.startsWith('7')) newValues.functionType = 'FUNDRAISING';
    }

    onCategoryChange(newValues);
  };

  // Get available subcategories based on main category
  const getSubcategories = useMemo(() => {
    const category = initialValues.accountCategory as keyof ChartOfAccounts;
    if (!category || !CHART_OF_ACCOUNTS[category]) {
      return [];
    }

    const categoryData = CHART_OF_ACCOUNTS[category];
    if (Array.isArray(categoryData)) {
      return categoryData.flatMap(item => 
        item.subcategories ? 
          item.subcategories.map(sub => ({
            code: sub.code,
            name: sub.name,
            parentCode: item.code
          })) : 
          [{ code: item.code, name: item.name }]
      );
    }

    // Handle expense categories which have a different structure
    if (category === 'Expenses') {
      return Object.entries(categoryData as ExpenseCategory).flatMap(([type, items]) =>
        items.flatMap(item =>
          item.subcategories?.map(sub => ({
            code: sub.code,
            name: sub.name,
            parentCode: item.code,
            type
          })) || []
        )
      );
    }

    return [];
  }, [initialValues.accountCategory]);

  // Get Form 990 categories based on account category
  const getForm990Categories = useMemo(() => {
    if (!initialValues.accountCategory) return [];

    switch (initialValues.accountCategory) {
      case 'Revenue':
        return [
          { value: 'REV100', label: 'Public Support', subcategories: {
            'REV101': 'Public Support',
            'REV102': 'Government Grants',
            'REV103': 'Private Grants',
            'REV104': 'Membership Dues'
          }},
          { value: 'REV200', label: 'Program Service Revenue', subcategories: {
            'REV201': 'Fee for Service',
            'REV202': 'Contract Revenue',
            'REV203': 'Government Fees'
          }}
        ];
      case 'Expenses':
        return [
          { value: 'PS100', label: 'Program Services', subcategories: {
            'PS101': 'Direct Program Expenses',
            'PS102': 'Program Staff Salaries',
            'PS103': 'Program Equipment & Supplies'
          }},
          { value: 'SS100', label: 'Supporting Services - Administrative', subcategories: {
            'SS101': 'Management Salaries',
            'SS102': 'Office Expenses',
            'SS103': 'Professional Fees'
          }},
          { value: 'SS200', label: 'Supporting Services - Fundraising', subcategories: {
            'SS201': 'Fundraising Staff',
            'SS202': 'Event Expenses',
            'SS203': 'Marketing & Communications'
          }}
        ];
      case 'Assets':
        return [
          { value: 'PART_X_1_15', label: 'Part X Line 1-15 (Current Assets)', subcategories: {} },
          { value: 'SCHEDULE_D', label: 'Schedule D (Fixed Assets)', subcategories: {} }
        ];
      case 'Liabilities':
        return [
          { value: 'PART_X_16_26', label: 'Part X Line 16-26 (Current Liabilities)', subcategories: {} },
          { value: 'SCHEDULE_D_LIAB', label: 'Schedule D (Long-term Liabilities)', subcategories: {} }
        ];
      case 'Net Assets':
        return [
          { value: 'PART_X_27_28', label: 'Part X Line 27-28 (Without Donor Restrictions)', subcategories: {} },
          { value: 'PART_X_29_33', label: 'Part X Line 29-33 (With Donor Restrictions)', subcategories: {} }
        ];
      default:
        return [];
    }
  }, [initialValues.accountCategory]);

  return (
    <CategoryPaper>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 3, 
        gap: 2 
      }}>
        <StyledIcon />
        <HeaderTypography 
          variant="h6" 
        >
          Accounting Categorization
        </HeaderTypography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <StyledTextField
            select
            fullWidth
            label="Account Category"
            value={initialValues.accountCategory || ''}
            onChange={(e) => handleChange('accountCategory', e.target.value)}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    backgroundColor: 'rgb(26, 31, 46)',
                    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(12px)',
                  }
                }
              }
            }}
          >
            {Object.keys(CHART_OF_ACCOUNTS).map((category) => (
              <StyledMenuItem key={category} value={category}>
                {category}
              </StyledMenuItem>
            ))}
          </StyledTextField>
        </Grid>

        <Grid item xs={12} md={4}>
          <StyledTextField
            select
            fullWidth
            label="Account Subcategory"
            value={initialValues.accountSubcategory || ''}
            onChange={(e) => handleChange('accountSubcategory', e.target.value)}
            disabled={!initialValues.accountCategory}
          >
            <StyledMenuItem value="">Select Subcategory</StyledMenuItem>
            {getSubcategories.map((sub) => (
              <StyledMenuItem key={sub.code} value={sub.code}>
                {sub.code} - {sub.name}
              </StyledMenuItem>
            ))}
          </StyledTextField>
        </Grid>

        <Grid item xs={12} md={4}>
          <StyledTextField
            select
            fullWidth
            label="Function Type"
            value={initialValues.functionType || ''}
            onChange={(e) => handleChange('functionType', e.target.value)}
            disabled={!initialValues.accountCategory || initialValues.accountCategory !== 'Expenses'}
          >
            <StyledMenuItem value="PROGRAM">Program Services</StyledMenuItem>
            <StyledMenuItem value="ADMIN">Administrative</StyledMenuItem>
            <StyledMenuItem value="FUNDRAISING">Fundraising</StyledMenuItem>
          </StyledTextField>
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledTextField
            select
            fullWidth
            label="Form 990 Category"
            value={initialValues.form990Category || ''}
            onChange={(e) => handleChange('form990Category', e.target.value)}
            disabled={!initialValues.accountCategory}
          >
            <StyledMenuItem value="">Select Form 990 Category</StyledMenuItem>
            {getForm990Categories.map((category) => (
              <StyledMenuItem 
                key={category.value} 
                value={category.value}
              >
                {category.label}
              </StyledMenuItem>
            ))}
          </StyledTextField>
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledTextField
            select
            fullWidth
            label="Fiscal Sponsorship Type"
            value={initialValues.fiscalSponsorshipType || ''}
            onChange={(e) => handleChange('fiscalSponsorshipType', e.target.value)}
          >
            <StyledMenuItem value="">Select Fiscal Sponsorship Type</StyledMenuItem>
            <StyledMenuItem value="PROGRAM">Program</StyledMenuItem>
            <StyledMenuItem value="ADMIN">Administrative</StyledMenuItem>
            <StyledMenuItem value="OTHER">Other</StyledMenuItem>
          </StyledTextField>
        </Grid>
      </Grid>
    </CategoryPaper>
  );
}; 